import { useMemo, useState } from "react";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";

import {
  formatCurrency,
  formatPercent,
  formatSignedCurrency,
} from "../../../../../utils";
import { InsightsSkeletonRows } from "../InsightsSkeletonRows";
import { RetryNote } from "../RetryNote";
import { buildAllocation, buildEarned } from "./insights";
import {
  AllocationFill,
  Body,
  Chevron,
  CollapseLabel,
  Column,
  ColumnEmpty,
  ColumnHeader,
  ColumnSubtitle,
  ColumnTitle,
  CompactLabel,
  CompactSubtitle,
  EarnedFill,
  EarnedLegend,
  EarnedRow,
  Header,
  HeaderSubtitle,
  HeaderTitle,
  LegendLabel,
  LegendSubtitle,
  Name,
  Net,
  NetValue,
  Panel,
  Row,
  RowList,
  Swatch,
  Tax,
  Track,
  Value,
} from "./styles";
import { SavingsInsightsProps } from "./types";

export const SavingsInsights: React.FC<SavingsInsightsProps> = ({
  savings,
  status,
  onRetry,
  isCompact,
}) => {
  const [expanded, setExpanded] = useState(!isCompact);

  const allocation = useMemo(() => buildAllocation(savings), [savings]);
  const earned = useMemo(() => buildEarned(savings), [savings]);

  const toggleExpanded = () => {
    setExpanded((current) => !current);
  };

  const collapseLabel = expanded ? "Collapse" : "Expand";

  const allocationRows = (
    <RowList>
      {allocation.map((item) => (
        <Row key={item.id}>
          <Name>
            <Swatch $color={item.color} />
            <span>{item.name}</span>
          </Name>
          <Track>
            <AllocationFill $width={item.percent} $color={item.color} />
          </Track>
          <Value>{formatPercent(item.percent)}</Value>
        </Row>
      ))}
    </RowList>
  );

  const earnedRows = (
    <RowList>
      {earned.map((item) => (
        <EarnedRow key={item.id}>
          <Name>
            <span>{item.name}</span>
          </Name>
          <Track>
            <EarnedFill $width={item.barWidth} $tone={item.tone} />
          </Track>
          <Value $tone={item.tone}>{formatSignedCurrency(item.value)}</Value>
          <Tax>
            <CompactLabel>Tax withheld </CompactLabel>
            {formatSignedCurrency(-item.taxWithheld)}
          </Tax>
          <Net>
            <CompactLabel>Net </CompactLabel>
            <NetValue>{formatCurrency(item.netReceived)}</NetValue>
          </Net>
        </EarnedRow>
      ))}
    </RowList>
  );

  const unavailableNote = (
    <ColumnEmpty>
      <RetryNote
        message="Couldn't load this period's figures."
        onRetry={onRetry}
      />
    </ColumnEmpty>
  );

  let allocationContent = allocationRows;
  let earnedContent = earnedRows;
  if (status === "loading") {
    allocationContent = <InsightsSkeletonRows label="Loading allocation" />;
    earnedContent = <InsightsSkeletonRows label="Loading gross interest" />;
  } else if (status === "unavailable") {
    allocationContent = unavailableNote;
    earnedContent = unavailableNote;
  }

  return (
    <Panel aria-busy={status === "loading"}>
      <Header
        type="button"
        onClick={toggleExpanded}
        aria-expanded={expanded}
        aria-label="Toggle insights section"
      >
        <Chevron $expanded={expanded}>
          <ExpandLessRoundedIcon />
        </Chevron>
        <HeaderTitle>Insights</HeaderTitle>
        <HeaderSubtitle>Allocation &amp; gross interest</HeaderSubtitle>
        <CollapseLabel>{collapseLabel}</CollapseLabel>
      </Header>

      {expanded && (
        <Body>
          <Column>
            <ColumnHeader>
              <ColumnTitle>Allocation</ColumnTitle>
              <ColumnSubtitle>
                By account · share of current value
              </ColumnSubtitle>
            </ColumnHeader>
            {allocationContent}
          </Column>

          <Column>
            <ColumnHeader>
              <ColumnTitle>Gross interest</ColumnTitle>
              <CompactSubtitle>
                Before tax by account · most to least
              </CompactSubtitle>
              <EarnedLegend>
                <LegendSubtitle>By account · most to least</LegendSubtitle>
                <LegendLabel>Gross</LegendLabel>
                <LegendLabel>Tax</LegendLabel>
                <LegendLabel>Net</LegendLabel>
              </EarnedLegend>
            </ColumnHeader>
            {earnedContent}
          </Column>
        </Body>
      )}
    </Panel>
  );
};
